/** @jsx jsx */
import React, { useMemo, useCallback, useEffect } from 'react';
import { jsx } from '@emotion/core';
import { InstallmentsOffer } from 'state/financeOffers';
import { monthlyPayment } from 'utils/finance';
import { map, sortBy, pipe, prop, find, propEq } from 'ramda';
import {
  Divider,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core';
import styles from '../styles';
import ExpandablePanel from 'components/ExpandablePanel';
import useTranslate, {
  useMarkdownTranslate,
} from 'hooks/useTranslate/useTranslate';

import { conformToMask, decimalMoneyMask } from 'utils/masks';
import { helpText } from 'features/Theme';
import Link from 'components/Link';
import Button from 'components/Button';

import useNavigationHelpers from 'hooks/useNavigationHelpers';
import ActionButtonsContainer from 'components/ActionButtonsContainer';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { useDispatch, useSelector } from 'react-redux';
import {
  mergeApplication,
  selectCreditApplication,
} from 'state/creditApplication';

import useAnalytics, { Event } from 'hooks/useAnalytics';
import useAnalyticVariables from 'hooks/useAnalyticVariables';
import BreadTitle from 'features/unified/OfferIntro/BreadTitle';
export interface InstallmentPaymentOptionProps {
  intendedPurchaseAmount: number;
  installmentOffers: InstallmentsOffer[];
  panelName: string;
  expanded: boolean;
  onChange: React.ComponentProps<typeof ExpandablePanel>['onChange'];
}

export interface InstallmentOfferData {
  id: string;
  perMonth: number;
  termLength: number;
  apr: number;
  total: number;
  interest: number;
  capacityValue: number;
}

export const getInstallmentOfferData = (
  principal: number,
  installmentOffer: InstallmentsOffer,
) => {
  const apr = installmentOffer.termRate / 100;
  const termLength = installmentOffer.termLength;
  const perMonth = monthlyPayment(principal, apr, termLength);
  const total = perMonth * termLength;
  const interest = total - principal;
  return {
    id: installmentOffer.id,
    perMonth,
    termLength,
    apr,
    total,
    interest,
    capacityValue: installmentOffer.capacityValue,
  };
};

const getInstallmentOffersData = (
  principal: number,
  offers: InstallmentsOffer[],
): InstallmentOfferData[] => {
  const installmentOffersData: InstallmentOfferData[] = pipe(
    map((financeOffer: InstallmentsOffer) => {
      return getInstallmentOfferData(principal, financeOffer);
    }),
    sortBy(prop('termLength')),
  )(offers);

  return installmentOffersData;
};

const InstallmentCheckout: React.FC<InstallmentPaymentOptionProps> = props => {
  const {
    panelName,
    expanded,
    intendedPurchaseAmount,
    installmentOffers,
    onChange,
  } = props;

  const installmentOffersData = useMemo(
    () => getInstallmentOffersData(intendedPurchaseAmount, installmentOffers),
    [installmentOffers, intendedPurchaseAmount],
  );

  const minPayment = useMemo(
    () => Math.min(...map(prop('perMonth'), installmentOffersData)),
    [installmentOffersData],
  );

  const maxCapacity = useMemo(() => {
    const result = Math.max(
      ...map(prop('capacityValue'), installmentOffersData),
    );
    if (result) {
      return conformToMask((result / 100).toFixed(2), decimalMoneyMask);
    } else {
      return '';
    }
  }, [installmentOffersData]);

  const { selectedOfferId } = useSelector(selectCreditApplication);

  const defaultSelectedOfferId = useMemo(() => {
    if (!selectedOfferId) {
      return '';
    }

    const selectedOffer = find(
      propEq('id', selectedOfferId),
      installmentOffers,
    );
    return selectedOffer ? selectedOffer.id : '';
  }, [installmentOffers, selectedOfferId]);

  const [
    selectedInstallmentOffer,
    setSelectedInstallmentOffer,
  ] = React.useState(defaultSelectedOfferId);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedInstallmentOffer((event.target as HTMLInputElement).value);
    },
    [setSelectedInstallmentOffer],
  );

  const { push } = useNavigationHelpers();
  const dispatch = useDispatch();

  const nextPath = useCallback(() => {
    dispatch(mergeApplication({ selectedOfferId: selectedInstallmentOffer }));
    push(
      `/unified/checkout/installments/place-order/${selectedInstallmentOffer}`,
    );
  }, [dispatch, push, selectedInstallmentOffer]);

  const t = useTranslate();
  const tMd = useMarkdownTranslate();

  const { sendEvents } = useAnalytics();
  const { setAnalyticsVariables } = useAnalyticVariables();

  useEffect(() => {
    expanded && sendEvents([Event.InstallmentOfferExpanded]);
  }, [expanded, sendEvents, setAnalyticsVariables]);

  const title = (
    <div>
      <div css={styles.paymentOption.summaryTitle}>
        <BreadTitle
          titlePart={tMd('unified.offer.paymentOption.installments.title')}
          logo={t('unified.offer.paymentOption.installments.breadLogo')}
          id={'installmentsBreadLogo'}
        />
      </div>
      <div>
        <div css={styles.paymentOption.summaryBody}>
          {/*Flexible monthly payment plans as low as{' '}
          <span css={styles.boldColorText}>$32.02/mo*</span>*/}
          {tMd('unified.offer.paymentOption.installments.description', {
            amount: `${conformToMask(
              minPayment.toFixed(2),
              decimalMoneyMask,
            )}/mo*`,
          })}
        </div>
      </div>
    </div>
  );
  const body = (
    <div css={styles.paymentOption.body}>
      <div css={styles.paymentOption.section}>
        <Divider />
      </div>
      <div css={styles.paymentOption.section}>
        <span css={helpText}>
          {tMd('unified.offer.paymentOption.installments.taxShippingCheckout')}
        </span>
      </div>
      <RadioGroup
        value={selectedInstallmentOffer}
        onChange={handleChange}
        aria-label="installment offers"
        name="radio-buttons-group"
      >
        {installmentOffersData.map((installmentOffer: InstallmentOfferData) => {
          return (
            <Box key={installmentOffer.id} css={styles.paymentOption.section}>
              <FormControlLabel
                value={installmentOffer.id}
                control={<Radio />}
                label={
                  <span>
                    <span css={styles.paymentOption.bigMoney}>
                      {conformToMask(
                        installmentOffer.perMonth.toFixed(2),
                        decimalMoneyMask,
                      )}
                    </span>
                    <span css={helpText}>/mo</span>
                  </span>
                }
              />

              <div>
                for {installmentOffer.termLength}{' '}
                {installmentOffer.termLength > 1 ? 'months' : 'month'}
                <span css={{ float: 'right' }}>
                  {(installmentOffer.apr * 100).toFixed(2)}% APR
                </span>
              </div>
              <div>
                {/* FIXME: Use translation */}
                Interest:{' '}
                {conformToMask(
                  installmentOffer.interest.toFixed(2),
                  decimalMoneyMask,
                )}
                <span css={{ float: 'right' }}>
                  Total:{' '}
                  {conformToMask(
                    installmentOffer.total.toFixed(2),
                    decimalMoneyMask,
                  )}
                </span>
              </div>
            </Box>
          );
        })}
      </RadioGroup>
      <p>
        <Link
          id="installmentLearnMore"
          to={'/unified/prequal-offer/installments/learn-more'}
          css={styles.chevronRightIcon}
        >
          {tMd('unified.offer.paymentOption.installments.learnMoreButton')}
          <ChevronRightIcon />
        </Link>
      </p>
    </div>
  );

  const footer = (
    <div css={styles.paymentOption.footer}>
      <Box mb={2}>
        <ActionButtonsContainer>
          <Button
            id={`installmentOffer-accept-button`}
            disabled={selectedInstallmentOffer === ''}
            mobileFullWidth={true}
            onClick={nextPath}
          >
            {tMd('unified.offer.paymentOption.installments.continue')}
          </Button>
        </ActionButtonsContainer>
      </Box>
    </div>
  );
  return (
    <ExpandablePanel
      expanded={expanded}
      panelName={panelName}
      title={title}
      children={body}
      footer={footer}
      onChange={onChange}
      banner={tMd('unified.offer.paymentOption.installments.banner', {
        amount: maxCapacity,
      })}
    />
  );
};

export default InstallmentCheckout;
